import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Trophy, Swords, Skull } from "lucide-react";
import Navbar from "../components/Navbar";
import GameCanvas from "../game/GameCanvas";
import SectorCompleteOverlay from "../components/SectorCompleteOverlay";
import BossIntro from "../components/BossIntro";
import CometIntro from "../components/CometIntro";
import TournamentMatchCompleteCard from "../components/TournamentMatchCompleteCard";
import api from "../lib/api";
import { Sounds } from "../lib/sound";
import { TOTAL_LEVELS, SECTORS, SECTOR_SIZE, getSector, getSignatureShape, isBossLevel, getLevel } from "../lib/levels";

export default function Play() {
  const { level } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const lvlNum = Math.max(1, Math.min(TOTAL_LEVELS, parseInt(level || "1", 10)));
  // Pull the challenge token from the URL deep-link `?challenge_id=...` so
  // that on level completion we can flag the challenge as responded and
  // notify the original challenger.
  const challengeId = searchParams.get("challenge_id") || null;
  const tournamentId = searchParams.get("tournament_id") || null;
  // Shared bracket replay — pilots arriving via a copied "replay link"
  // (e.g. /play/12?replay_seed=abcd...) get a REPLAY MODE badge and the
  // seed echoed in the HUD. This is a casual social feature: it doesn't
  // count toward unlocks, isn't submitted to leaderboards, and is
  // intentionally outside the wager/tournament pipeline.
  const replaySeed = searchParams.get("replay_seed") || null;
  // Determine mode for artifact-power gating. Wagers and tournaments
  // disable artifact powers to keep PvP brackets fair.
  const playMode = tournamentId
    ? "tournament"
    : challengeId
      ? "wager"
      : "campaign";
  const [progress, setProgress] = useState(null);
  const [sectorCutscene, setSectorCutscene] = useState(null); // {fromSector, nextSector, signatureShape}
  // Live tournament opponent score — polled every 3s while playing a
  // tournament match. Drives the HUD pill at the top of the canvas
  // so the pilot has real-time awareness of whether they're ahead
  // or behind. `null` = no opponent yet, `undefined` = haven't loaded.
  const [oppLive, setOppLive] = useState(null);
  // Tournament match result — populated once the pilot has submitted a
  // score in tournament mode. When set, the GameCanvas is hidden and
  // the daily-style <TournamentMatchCompleteCard /> takes its place so
  // the post-match UX mirrors /daily's "Run cleared" lock screen.
  // Shape: { myScore, oppScore|null, oppName, oppIsBot, outcome: "win"|"lose"|"waiting" }
  const [tournamentResult, setTournamentResult] = useState(null);
  // Campaign mid-attempt checkpoint — surfaces a gold "RESUME" banner
  // above the canvas when the pilot returns to a level they died on
  // mid-attempt. Only renders for plain campaign mode (no tournament,
  // no wager, no replay) and only when checkpoint.level matches the
  // current lvlNum. Same softer-recovery contract as Daily: bricks
  // reset on resume, lives + score carry over.
  const [campaignCheckpoint, setCampaignCheckpoint] = useState(null);
  const [campaignCheckpointDismissed, setCampaignCheckpointDismissed] = useState(false);
  const [campaignResumeKey, setCampaignResumeKey] = useState(0);
  // Stable fullscreen root: survives GameCanvas remounts when the player clicks
  // "Next Sector" so native fullscreen isn't dropped between levels.
  const fsRootRef = useRef(null);

  useEffect(() => {
    api
      .get("/progress")
      .then((r) => {
        setProgress(r.data);
        if (lvlNum > (r.data.unlocked_level || 1)) navigate("/levels", { replace: true });
      })
      .catch(() => setProgress({ unlocked_level: 1, high_scores: {}, total_score: 0 }));
  }, [lvlNum, navigate]);

  // Campaign mid-attempt checkpoint fetch. Only relevant for plain
  // campaign mode (skip tournament/wager/replay). Refetched on level
  // change so the resume CTA only shows when checkpoint.level matches
  // the level the pilot just navigated to.
  useEffect(() => {
    if (playMode !== "campaign" || replaySeed) {
      setCampaignCheckpoint(null);
      return;
    }
    api
      .get("/campaign/checkpoint")
      .then((r) => {
        const cp = r.data?.checkpoint || null;
        // Only surface the banner when the saved checkpoint matches
        // the level the pilot is currently on. If they're on a
        // different level, ignore (the row will overwrite or expire).
        setCampaignCheckpoint(cp && cp.level === lvlNum ? cp : null);
      })
      .catch(() => setCampaignCheckpoint(null));
    setCampaignCheckpointDismissed(false);
    setCampaignResumeKey(0);
  }, [lvlNum, playMode, replaySeed]);

  const showCampaignResumeCta =
    !!campaignCheckpoint &&
    !campaignCheckpointDismissed &&
    campaignCheckpoint.level === lvlNum &&
    !tournamentResult;
  const resumeFromCampaignCheckpoint = useCallback(() => {
    if (!campaignCheckpoint) return;
    setCampaignResumeKey((k) => k + 1);
    setCampaignCheckpointDismissed(true);
  }, [campaignCheckpoint]);
  const dismissCampaignCheckpoint = useCallback(() => {
    setCampaignCheckpointDismissed(true);
    api.delete("/campaign/checkpoint").catch(() => {});
    setCampaignCheckpoint(null);
  }, []);

  // One-shot toast on mount when the pilot arrives via a challenge deep-link
  // so they know what's at stake.
  useEffect(() => {
    if (challengeId) {
      toast(`Challenge accepted · Level ${lvlNum}`, {
        description: "Clear it to ping your challenger with your score.",
        icon: <Swords className="w-4 h-4 text-[#ffd700]" />,
        duration: 4000,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challengeId]);

  // First-time-audio FTUE — when a brand-new pilot lands on their very
  // first /play/* page, briefly auto-open the audio panel for ~3s so
  // they discover the volume controls + per-sector music exist. Gated
  // by a localStorage flag so it never repeats. Skipped on tournament /
  // replay / challenge deep-links to avoid stealing focus from those
  // contextual toasts.
  useEffect(() => {
    if (tournamentId || challengeId || replaySeed) return undefined;
    if (typeof window === "undefined") return undefined;
    try {
      if (localStorage.getItem("cwb_audio_ftue_shown") === "1") return undefined;
      // Set the flag INSIDE the timeout (not before) so React 18
      // StrictMode's double-mount in dev doesn't cancel the first run
      // and then early-return the second run because the flag was set.
      const t = window.setTimeout(() => {
        if (localStorage.getItem("cwb_audio_ftue_shown") === "1") return;
        localStorage.setItem("cwb_audio_ftue_shown", "1");
        window.dispatchEvent(new CustomEvent("cwb-show-audio-hint"));
      }, 1500);
      return () => window.clearTimeout(t);
    } catch {
      return undefined;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Shared bracket-replay landing toast — surfaces once when a pilot
  // opens a /play link carrying ?replay_seed=<seed>. Lets them know
  // the run is a casual replay (no progress/wager side effects).
  useEffect(() => {
    if (replaySeed) {
      const short = `${replaySeed.slice(0, 6)}…${replaySeed.slice(-4)}`;
      toast(`Replay seed · ${short}`, {
        description: "Same RNG as the original bracket. Casual run — no progression side-effects.",
        icon: <Trophy className="w-4 h-4 text-[#b026ff]" />,
        duration: 5000,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replaySeed]);

  // Live opponent-score poll for tournament matches. Runs every 3s
  // until either (a) the bracket completes, (b) the opponent's score
  // is in, or (c) the user leaves the page. Surfaces `oppLive` so
  // the HUD pill at the top of the canvas can render the live score.
  useEffect(() => {
    if (!tournamentId) return undefined;
    let cancelled = false;
    let meId = null;
    const tick = async () => {
      try {
        // Cache our user id once — needed to figure out which seat is
        // "me" vs "opponent" in the match. Live polling reuses the
        // cached id so we make at most one /auth/me round-trip per
        // tournament visit.
        if (!meId) {
          try {
            const meR = await api.get("/auth/me");
            meId = meR.data?.id || null;
          } catch { /* keep null, fall through */ }
        }
        const r = await api.get(`/tournaments/${tournamentId}`);
        if (cancelled) return;
        const t = r.data;
        // Prefer my_match (active R1), fall back to scanning matches[]
        // for the pilot's most recent entry — the active-match helper
        // skips resolved matches, but we still want to upgrade the
        // post-submit lock card once the opp's score lands.
        let mm = t?.my_match;
        if (!mm && meId && Array.isArray(t?.matches)) {
          const mine = t.matches.filter(
            (m) => m && (m.pa === meId || m.pb === meId),
          );
          mine.sort((a, b) => (b.round || 0) - (a.round || 0));
          mm = mine[0] || null;
        }
        if (!mm || !meId) {
          setOppLive(null);
          return;
        }
        // Determine which seat is me + extract opponent's score.
        const onPaSide = mm.pa === meId;
        const oppId = onPaSide ? mm.pb : mm.pa;
        const oppScore = onPaSide ? mm.score_b : mm.score_a;
        const opp = (t.participants || []).find((p) => p.user_id === oppId);
        setOppLive({
          score: oppScore,         // null if opponent hasn't submitted
          name: opp?.name || "Opponent",
          is_bot: !!opp?.is_bot,
          waiting: oppScore === null,
        });
        // If we've already submitted (tournamentResult set with "waiting"
        // outcome) and the match now has a resolved winner, upgrade the
        // lock card from waiting → win/lose so the pilot sees the final
        // verdict without manually navigating to the bracket page.
        if (mm.winner) {
          setTournamentResult((prev) => {
            if (!prev || prev.outcome !== "waiting") return prev;
            return {
              ...prev,
              oppScore,
              outcome: mm.winner === meId ? "win" : "lose",
            };
          });
        }
      } catch {
        /* keep last value, retry next tick */
      }
    };
    tick();
    const id = setInterval(tick, 3000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [tournamentId]);



  const best = progress?.high_scores?.[String(lvlNum)] ?? 0;

  const onComplete = async (score, metrics = {}) => {
    try {
      const r = await api.post("/progress/complete", {
        level: lvlNum,
        score,
        challenge_id: challengeId || undefined,
        ...metrics,
      });
      setProgress(r.data);
      // Tournament submission — fires only on tournament play.
      // Submits the score to the bracket, surfaces an outcome toast
      // ("YOU WIN" / "YOU LOSE" / "Waiting on Opponent"), and stops
      // here so the rest of the campaign-mode unlocks (sector
      // cutscenes, achievements) don't run twice.
      if (tournamentId) {
        try {
          const sr = await api.post(`/tournaments/${tournamentId}/submit`, {
            score,
          });
          const tt = sr.data;
          let meR;
          try { meR = await api.get("/auth/me"); } catch { meR = null; }
          const meId = meR?.data?.id;
          // Find the pilot's match. Prefer `my_match` (active R1 match),
          // but the backend's `_match_for_user` SKIPS resolved matches —
          // so for a 2-pilot vs-bot bracket that completes at submit
          // time, my_match is null. Fall back to scanning matches[] for
          // the pilot's most recent entry so the lock card can still
          // resolve win/lose instead of sticking on "waiting".
          let mm = tt?.my_match;
          if (!mm && meId && Array.isArray(tt?.matches)) {
            const mine = tt.matches.filter(
              (m) => m && (m.pa === meId || m.pb === meId),
            );
            // Pick the highest-round (latest) match the pilot played in.
            mine.sort((a, b) => (b.round || 0) - (a.round || 0));
            mm = mine[0] || null;
          }
          if (mm && meId) {
            const iAmPa = mm.pa === meId;
            const myScore = iAmPa ? mm.score_a : mm.score_b;
            const oppScore = iAmPa ? mm.score_b : mm.score_a;
            const oppId = iAmPa ? mm.pb : mm.pa;
            const oppParticipant =
              (tt.participants || []).find((p) => p.user_id === oppId);
            const oppName = oppParticipant?.name || "Opponent";
            const oppIsBot = !!oppParticipant?.is_bot;
            const fmt = (v) => (v == null ? "—" : Number(v).toLocaleString());
            // Determine outcome for the daily-style match-complete card.
            let outcome = "waiting";
            if (mm.winner === meId) outcome = "win";
            else if (mm.winner && mm.winner !== meId) outcome = "lose";
            setTournamentResult({
              myScore: myScore ?? score,
              oppScore,
              oppName,
              oppIsBot,
              outcome,
            });
            if (outcome === "win") {
              toast.success(`YOU WIN · ${fmt(myScore)} vs ${fmt(oppScore)}`, {
                description: `Beat ${oppName} — advancing in the bracket.`,
                icon: <Trophy className="w-4 h-4 text-[#ffd700]" />,
                duration: 7000,
                action: {
                  label: "Bracket",
                  onClick: () => navigate(`/tournaments/${tournamentId}`),
                },
              });
            } else if (outcome === "lose") {
              toast.error(`YOU LOSE · ${fmt(myScore)} vs ${fmt(oppScore)}`, {
                description: `${oppName} took the match.`,
                icon: <Skull className="w-4 h-4 text-[#ff007f]" />,
                duration: 7000,
                action: {
                  label: "Bracket",
                  onClick: () => navigate(`/tournaments/${tournamentId}`),
                },
              });
            } else {
              toast(`Score submitted · ${fmt(myScore)}`, {
                description: `Waiting on ${oppName}…`,
                icon: <Swords className="w-4 h-4 text-[#3aa8ff]" />,
                duration: 6000,
                action: {
                  label: "Bracket",
                  onClick: () => navigate(`/tournaments/${tournamentId}`),
                },
              });
            }
          } else {
            // Couldn't resolve a match at all (e.g. spectator-cached
            // payload, no meId). Still flip to the lock card so the
            // canvas doesn't linger — pilot can refresh from the
            // bracket page to see the resolved outcome.
            setTournamentResult({
              myScore: score,
              oppScore: null,
              oppName: "Opponent",
              oppIsBot: false,
              outcome: "waiting",
            });
          }
        } catch (err) {
          toast.error("Tournament submit failed", {
            description: err?.response?.data?.detail || "Try again from the bracket page.",
          });
        }
        return; // Tournament mode — skip campaign cutscenes/achievements below.
      }
      // Prismatic-tier artifact drop on first-clear sector bosses
      // (500 / 1000 / ...). Backend gates this so we just need to
      // surface it once per drop.
      if (r.data.prismatic_drop) {
        const pd = r.data.prismatic_drop;
        setTimeout(() => {
          toast.success(`${pd.icon} PRISMATIC DROP — ${pd.name}`, {
            description: "Boss-only loot. Equip it on /artifacts.",
            icon: <Trophy className="w-4 h-4 text-[#b026ff]" />,
            duration: 9000,
            action: {
              label: "View",
              onClick: () => navigate("/artifacts"),
            },
          });
        }, 1200);
      }
      // Prismatic ball-skin drop — cosmetic counterpart, same boss-
      // kill-only gating. Routes to /skins so pilots can equip it.
      if (r.data.prismatic_skin_drop) {
        const ps = r.data.prismatic_skin_drop;
        setTimeout(() => {
          toast.success(`PRISMATIC SKIN — ${ps.name}`, {
            description: "Rainbow boss-drop ball skin. Equip it on /skins.",
            icon: <Trophy className="w-4 h-4 text-[#ff007f]" />,
            duration: 9000,
            style: {
              borderLeftColor: "#ff007f",
              borderLeftWidth: 3,
              boxShadow: "0 0 24px rgba(255,0,127,0.35)",
            },
            action: {
              label: "Equip",
              onClick: () => navigate("/skins"),
            },
          });
        }, 1400);
      }
      // League promotion celebration — only fires when the pilot's
      // unlocked_level crossed a tier threshold on this completion.
      if (r.data.league_promoted) {
        const lp = r.data.league_promoted;
        setTimeout(() => {
          toast.success(`PROMOTED · ${lp.to.label.toUpperCase()} TIER`, {
            description: `${lp.from.label} → ${lp.to.label}. Skill-locked tournaments now welcome you.`,
            icon: <Trophy className="w-4 h-4" style={{ color: lp.to.color }} />,
            duration: 9000,
            style: {
              borderLeftColor: lp.to.color,
              borderLeftWidth: 3,
              boxShadow: `0 0 24px ${lp.to.color}44`,
            },
            action: {
              label: "Brackets",
              onClick: () => navigate("/tournaments"),
            },
          });
        }, 1500);
      }
      const newly = r.data.newly_unlocked_achievements || [];
      newly.forEach((a, idx) => {
        setTimeout(() => {
          // If the trophy unlocked a comet skin, surface a one-tap "Equip"
          // action right on the toast so the pilot can flex it immediately
          // without navigating away. Sonner runs the action callback +
          // auto-dismisses on click.
          const equipAction = a.reward_skin
            ? {
                label: "Equip",
                onClick: () => {
                  api
                    .post("/ball-skins/active", { skin_id: a.reward_skin })
                    .then(() => {
                      toast.success("Equipped!", {
                        description: `${a.reward_skin.replace(/-/g, " ")} is now your active comet.`,
                        icon: <Trophy className="w-4 h-4 text-[#ffd700]" />,
                        duration: 3500,
                      });
                    })
                    .catch(() => {
                      toast.error("Couldn't equip skin", {
                        description: "Open Skins → Comets to equip manually.",
                        duration: 3500,
                      });
                    });
                },
              }
            : undefined;
          toast.success(`${a.name}${a.featured ? " · 2×" : ""}`, {
            description: `${a.desc} · +${a.reward_score.toLocaleString()} pts${a.featured ? " (weekly bonus)" : ""}`,
            icon: <Trophy className="w-4 h-4 text-[#ffd700]" />,
            duration: a.reward_skin ? 7500 : 5000,
            action: equipAction,
          });
        }, idx * 400);
      });
      // If we cleared a level via a challenge deep-link, surface a confirmation
      // toast so the pilot knows the challenger has been pinged with the score.
      if (challengeId) {
        setTimeout(() => {
          toast.success("Challenger notified", {
            description: `Your score of ${Number(score).toLocaleString()} was sent back.`,
            icon: <Swords className="w-4 h-4 text-[#ffd700]" />,
            duration: 4500,
          });
        }, (newly.length || 0) * 400);
      }
      // Boss-defeat celebration — fires for every sector boundary level
      // (500, 1000, ..., 3000). Fires before the sector cutscene so the
      // pilot reads the boss kill, then watches the cutscene reveal the
      // next sector. The tier is mirrored from GameCanvas's camera-shake
      // brackets (<30s blitz, 30-60s decisive, >60s grind) so the toast
      // language matches the screen-shake intensity the pilot just felt.
      if (isBossLevel(lvlNum)) {
        const bossName = getLevel(lvlNum).boss?.name || "Sector Boss";
        const durMs = Number(metrics?.duration_ms || 0);
        const durSec = durMs > 0 ? Math.max(1, Math.round(durMs / 1000)) : null;
        let tierLabel = "Sector boss cleared";
        if (durSec !== null) {
          if (durMs < 30000) tierLabel = `Crushing Victory · ${durSec}s`;
          else if (durMs > 60000) tierLabel = `Hard Fought · ${durSec}s`;
          else tierLabel = `Decisive · ${durSec}s`;
        }
        // Personal-best — server flags boss_pb on the response when the
        // just-completed kill set a new fastest time. Append a gold "PB"
        // badge to the tier label so the pilot reads the speedrun call-out
        // alongside the existing tier flair.
        const isPb = !!r.data?.boss_pb;
        if (isPb) tierLabel = `${tierLabel} · PB`;
        Sounds.bossDefeat();
        toast.success(`${bossName} defeated!`, {
          description: `${tierLabel} · +${Number(score).toLocaleString()} pts`,
          icon: <Skull className="w-4 h-4 text-[#ff007f]" />,
          duration: 6000,
        });
      }
      // Trigger sector-complete cinematic when the current level is the last
      // of its sector AND the pilot just advanced past it (unlocked goes up).
      const isLastOfSector = lvlNum % SECTOR_SIZE === 0;
      if (isLastOfSector) {
        const fromSector = getSector(lvlNum);
        const nextSector = SECTORS.find((s) => s.from === lvlNum + 1) || null;
        const signatureShape = nextSector ? getSignatureShape(nextSector.id) : null;
        setSectorCutscene({ fromSector, nextSector, signatureShape });
      }
    } catch (e) {
      /* ignore, local state wins */
    }
  };

  // Seamless level transition target. GameCanvas calls this ~750ms after
  // the board is cleared on a regular campaign level — we navigate to the
  // next level which remounts GameCanvas via the `key={lvlNum}` prop and
  // keeps the pilot in the flow without an interstitial overlay.
  //
  // Sector-boundary levels (every 500) are gated: if a `sectorCutscene` is
  // queued, the SectorCompleteOverlay component owns navigation and fires
  // it from its onDismiss handler instead. This preserves the big "Warping
  // into <Sector> space" cinematic the user explicitly wants to keep at
  // sector boundaries (the noisy in-canvas "Sector Cleared" text is gone;
  // the dedicated cutscene remains).
  // Wagers (challengeId) and tournaments are intentionally left to the
  // legacy stop-overlay since the pilot needs to see their final score
  // before the round closes.
  const onAdvance = useCallback(() => {
    if (lvlNum % SECTOR_SIZE === 0) return; // SectorCompleteOverlay handles it
    if (lvlNum >= TOTAL_LEVELS) return;     // final level — show victory overlay
    if (challengeId || tournamentId) return; // wager / tournament — stop on score
    navigate(`/play/${lvlNum + 1}`);
  }, [lvlNum, challengeId, tournamentId, navigate]);

  const onSectorCutsceneDismiss = () => {
    setSectorCutscene(null);
    if (lvlNum < TOTAL_LEVELS && !challengeId && !tournamentId) {
      navigate(`/play/${lvlNum + 1}`);
    }
  };

  const onExit = () => navigate("/levels");

  // When the pilot lands on a boss level, mount the BossIntro overlay.
  // It self-dismisses after ~1.4s. Keyed on lvlNum so navigating between
  // bosses re-fires the cinematic.
  const bossOnThisLevel = isBossLevel(lvlNum) ? getLevel(lvlNum) : null;
  // Kill count for the social-proof line — fetched once per boss level.
  const [bossKillCount, setBossKillCount] = useState(null);
  useEffect(() => {
    if (!bossOnThisLevel) return;
    setBossKillCount(null);
    api.get("/leaderboard/boss-kills")
      .then((r) => {
        const items = r.data?.items || [];
        const match = items.find((i) => i.level === lvlNum);
        if (match) setBossKillCount(match.kills);
      })
      .catch(() => setBossKillCount(null));
  }, [lvlNum, bossOnThisLevel]);

  return (
    <div className="relative min-h-screen">
      <div className="starfield" />
      <Navbar />
      <main className="relative z-10 max-w-7xl mx-auto px-4 pt-24 pb-10 fade-in">
        {replaySeed && (
          <div
            className="mb-3 inline-flex items-center gap-2 px-3 py-1.5 border border-[#b026ff]/60 bg-[#b026ff]/10 font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-[#b026ff]"
            data-testid="play-replay-badge"
          >
            <Trophy className="w-3 h-3" />
            REPLAY · seed {replaySeed.slice(0, 6)}…{replaySeed.slice(-4)}
          </div>
        )}
        <div ref={fsRootRef} data-testid="play-fs-root" className="fs-root">
          {tournamentResult ? (
            <TournamentMatchCompleteCard
              tournamentId={tournamentId}
              level={lvlNum}
              myScore={tournamentResult.myScore}
              oppScore={tournamentResult.oppScore}
              oppName={tournamentResult.oppName}
              oppIsBot={tournamentResult.oppIsBot}
              outcome={tournamentResult.outcome}
            />
          ) : (
            <>
              {/* Campaign mid-attempt resume banner. Same softer-
                  recovery contract as Daily: bricks reset, lives +
                  score carry over from the checkpoint. */}
              {showCampaignResumeCta && (
                <div
                  className="mb-3 px-4 py-3 border border-[#ffd700]/40 bg-[#ffd700]/[0.06] flex flex-wrap items-center justify-between gap-3"
                  style={{ boxShadow: "0 0 14px rgba(255,215,0,0.18)" }}
                  data-testid="campaign-resume-banner"
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-mono-cosmic text-[10px] tracking-[0.3em] text-[#ffd700] uppercase">
                      // ATTEMPT RECOVERED · LEVEL {campaignCheckpoint.level}
                    </div>
                    <div className="text-white/80 text-sm mt-0.5">
                      Carry over{" "}
                      <span className="text-[#ffd700] font-bold tabular-nums">
                        {campaignCheckpoint.score.toLocaleString()}
                      </span>{" "}
                      pts ·{" "}
                      <span className="text-[#ff66cc] font-bold tabular-nums">
                        {campaignCheckpoint.lives}
                      </span>{" "}
                      {campaignCheckpoint.lives === 1 ? "life" : "lives"} (bricks reset).
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      type="button"
                      onClick={resumeFromCampaignCheckpoint}
                      className="px-4 py-2 bg-[#ffd700] text-black font-mono-cosmic text-xs tracking-[0.25em] uppercase font-bold hover:bg-[#fff7d6] transition"
                      data-testid="campaign-resume-continue"
                    >
                      Continue
                    </button>
                    <button
                      type="button"
                      onClick={dismissCampaignCheckpoint}
                      className="px-3 py-2 border border-white/25 hover:border-white/60 text-white/70 hover:text-white font-mono-cosmic text-xs tracking-[0.25em] uppercase transition"
                      data-testid="campaign-resume-dismiss"
                    >
                      Restart Fresh
                    </button>
                  </div>
                </div>
              )}
              <GameCanvas
                key={`${lvlNum}-${campaignResumeKey}`}
                level={lvlNum}
                campaignStartLives={
                  campaignResumeKey > 0 && campaignCheckpoint
                    ? campaignCheckpoint.lives
                    : null
                }
                campaignStartScore={
                  campaignResumeKey > 0 && campaignCheckpoint
                    ? campaignCheckpoint.score
                    : null
                }
                onComplete={onComplete}
                onExit={onExit}
                onAdvance={onAdvance}
                bestScore={best}
                fsTargetRef={fsRootRef}
                mode={playMode}
                forceBallSkin={tournamentId ? "cosmic-comet" : null}
                opponentLive={oppLive}
              />
            </>
          )}
        </div>
      </main>
      {bossOnThisLevel && (
        <BossIntro
          bossName={bossOnThisLevel.boss?.name || "Sector Boss"}
          sectorName={getSector(lvlNum).name}
          bossKey={lvlNum}
          killCount={bossKillCount}
        />
      )}
      {sectorCutscene && (
        <SectorCompleteOverlay
          fromSector={sectorCutscene.fromSector}
          nextSector={sectorCutscene.nextSector}
          signatureShape={sectorCutscene.signatureShape}
          onDismiss={onSectorCutsceneDismiss}
        />
      )}
      <CometIntro />
    </div>
  );
}
